import {
  boolean,
  check,
  foreignKey,
  index,
  jsonb,
  pgTable,
  text,
  timestamp,
  uuid,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { pages, pageReviews, pageVersions } from "./page-builder";

const tz = { withTimezone: true } as const;

export const PAGE_REVIEW_COMMENT_ANCHOR_TYPES = ["page", "component"] as const;

export type PageReviewCommentAnchorType =
  (typeof PAGE_REVIEW_COMMENT_ANCHOR_TYPES)[number];

export const pageReviewComments = pgTable(
  "page_review_comments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    pageId: uuid("page_id")
      .notNull()
      .references(() => pages.id, { onDelete: "cascade" }),
    pageVersionId: uuid("page_version_id")
      .notNull()
      .references(() => pageVersions.id, { onDelete: "cascade" }),
    anchorType: text("anchor_type").notNull().default("component"),
    componentId: text("component_id"),
    componentType: text("component_type"),
    content: text("content").notNull(),
    authorId: text("author_id").notNull(),
    authorName: text("author_name").notNull(),
    authorEmail: text("author_email"),
    resolvedAt: timestamp("resolved_at", tz),
    resolvedBy: text("resolved_by"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),

    // Review-flow columns (nullable for backward compat with inline comments)
    reviewId: uuid("review_id").references(() => pageReviews.id, {
      onDelete: "cascade",
    }),
    anchorLabel: text("anchor_label"),
    position: jsonb("position"),
    body: text("body"),
    authorRole: text("author_role"),
    resolved: boolean("resolved").default(false),
    parentId: uuid("parent_id"),
  },
  (table) => [
    index("idx_page_review_comments_page_created").on(
      table.tenantId,
      table.pageId,
      table.createdAt,
    ),
    index("idx_page_review_comments_component").on(
      table.tenantId,
      table.pageId,
      table.componentId,
      table.createdAt,
    ),
    index("idx_page_review_comments_resolved").on(
      table.tenantId,
      table.pageId,
      table.resolvedAt,
    ),
    index("idx_page_review_comments_review").on(
      table.tenantId,
      table.reviewId,
      table.createdAt,
    ),
    index("idx_page_review_comments_parent").on(table.parentId),
    foreignKey({
      columns: [table.parentId],
      foreignColumns: [table.id],
      name: "page_review_comments_parent_id_page_review_comments_id_fk",
    }).onDelete("cascade"),
    check(
      "chk_page_review_comments_anchor_type",
      sql`${table.anchorType} IN ('page', 'component')`,
    ),
    check(
      "chk_page_review_comments_component_anchor",
      sql`(
        (${table.anchorType} = 'page' AND ${table.componentId} IS NULL)
        OR
        (${table.anchorType} = 'component' AND ${table.componentId} IS NOT NULL)
      )`,
    ),
  ],
);
